import React from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import SimpleTableContent from 'src/components/simple-table-content';
import { noop } from 'lodash';

const FIT_SORT_MAP = {
  operationId: 'operationId',
  login: 'login',
  role: 'role',
  technology: 'technology',
  gu: 'gu',
  pdm: 'pdm',
  positionMap: 'positionMap',
  fitDate: 'fitDate',
  matchDate: 'matchDate',
  fitBy: 'fitBy',
  lastUpdate: 'lastUpdate',
};

/**
 * Fits History table component.
 * @param {Object} props
 * @param {Array} props.data
 * @param {Object} props.pagination
 * @param {boolean} props.loading
 * @param {Function} props.onPaginationChange
 * @param {Function} props.onSortChange
 * @returns {JSX.Element}
 */
function FitHistoryTable({ data, pagination, loading, onPaginationChange, onSortChange }) {
  const { t } = useTranslation();

  const columns = [
    {
      title: t('reports.fitOperationId'),
      dataIndex: 'operationId',
      width: 80,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text ? text.split('-')[0] : '-'} />,
    },
    {
      title: 'Login',
      dataIndex: 'login',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('role'),
      dataIndex: 'role',
      width: 150,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('tech'),
      dataIndex: 'technology',
      width: 150,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: 'GU',
      dataIndex: 'gu',
      width: 120,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: 'PDM',
      dataIndex: 'pdm',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('positionMap'),
      dataIndex: 'positionMap',
      width: 130,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.fitDate'),
      dataIndex: 'fitDate',
      width: 110,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.matchDate'),
      dataIndex: 'matchDate',
      width: 110,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.fitBy'),
      dataIndex: 'fitBy',
      width: 120,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.vagaDeFit'),
      dataIndex: 'need',
      width: 200,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.lastUpdate'),
      dataIndex: 'lastUpdate',
      width: 110,
      sorter: true,
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.comments'),
      dataIndex: 'comments',
      width: 250,
      align: 'left',
      onCell: () => ({ style: { verticalAlign: 'top', whiteSpace: 'pre-line', textAlign: 'left' } }),
      onHeaderCell: () => ({ style: { textAlign: 'left' } }),
      render: (text) => <SimpleTableContent mainContent={text} />,
    },
    {
      title: t('reports.cancelCategory'),
      dataIndex: 'cancelCategory',
      width: 160,
      render: (text) => <SimpleTableContent mainContent={text || '-'} />,
    },
    {
      title: t('reports.cancelSubcategory'),
      dataIndex: 'cancelSubcategory',
      width: 200,
      onCell: () => ({ style: { whiteSpace: 'pre-line' } }),
      render: (text) => <SimpleTableContent mainContent={text || '-'} />,
    },
    {
      title: t('reports.rollbackCategory'),
      dataIndex: 'rollbackCategory',
      width: 160,
      render: (text) => <SimpleTableContent mainContent={text || '-'} />,
    },
    {
      title: t('reports.rollbackSubcategory'),
      dataIndex: 'rollbackSubcategory',
      width: 200,
      onCell: () => ({ style: { whiteSpace: 'pre-line' } }),
      render: (text) => <SimpleTableContent mainContent={text || '-'} />,
    },
  ];

  const paginationConfig = {
    current: pagination.currentPage,
    total: pagination.totalCount,
    pageSize: pagination.limit,
    onChange: onPaginationChange,
    showTotal: (total) => `${total} ${t('reports.recordsFound')}`,
  };

  const handleTableChange = (_, __, sorter) => {
    const sort = FIT_SORT_MAP[sorter.field];
    const order = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
    onSortChange(sort && order ? { sort, order } : {});
  };

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey='operationId'
      loading={loading}
      pagination={paginationConfig}
      scroll={{ x: 1800 }}
      onChange={handleTableChange}
    />
  );
}

FitHistoryTable.defaultProps = {
  data: [],
  pagination: { currentPage: 1, totalCount: 0, limit: 10 },
  loading: false,
  onPaginationChange: noop,
  onSortChange: noop,
};

export default FitHistoryTable;
